import React, { useEffect, useState, useRef, useCallback } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PageTableStatusModal from "./PageTableStatusModal";
import { MdSummarize, MdChatBubbleOutline } from "react-icons/md";
import { FaCheckSquare, FaRegSquare, FaStop, FaPlay } from "react-icons/fa";

dayjs.extend(utc);
dayjs.extend(timezone);

export const getApiTokenFromLocalstorage = () => {
  const apiToken = localStorage.getItem("apiAuthToken");
  if (!apiToken || apiToken === "undefined") {
    console.warn("Invalid token found in localStorage.");
  }
  return apiToken;
};

export const globalDateFormat1 = () => {
  const globalDateFormat1 = "MM/DD/YYYY";
  return globalDateFormat1;
};

export const globalDateFormat2 = () => {
  const globalDateFormat2 = "MMMM DD, YYYY";
  return globalDateFormat2;
};

export const formatDate2 = (dateString2, dateType) => {
  const date = dateType === "current" ? dayjs() : dayjs(dateString2);
  if (!date.isValid()) return "Invalid Date";
  return date.format(globalDateFormat2());
};

export const sortedData = (rawData) => {
  return [...rawData].sort((a, b) => {
    const dateA = dayjs(a.AppointmentDate);
    const dateB = dayjs(b.AppointmentDate);

    if (dateB.isValid() && dateA.isValid()) {
      if (dateA.diff(dateB) !== 0) return dateA.diff(dateB); // Ascending by date
    }

    const timeA = dayjs(a.AppointmentTime, "HH:mm");
    const timeB = dayjs(b.AppointmentTime, "HH:mm");

    if (timeA.isValid() && timeB.isValid()) {
      if (timeA.diff(timeB) !== 0) return timeA.diff(timeB); // Ascending by time
    }

    const nameA = (a.PatientName || "").toLowerCase();
    const nameB = (b.PatientName || "").toLowerCase();
    return nameA.localeCompare(nameB); // Ascending by name
  });
};

export const formatPatientDetails = (appointId, appointName) => {
  return (
    <div>
      <div className="text-sm font-medium text-gray-900">{appointId}</div>
      <div className="text-sm text-gray-900">{appointName}</div>
    </div>
  );
};

export const formatMobileNumber = (phone) => {
  if (!phone) return "N/A";

  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
      6
    )}`;
  } else if (digits.length > 10) {
    const countryCode = digits.slice(0, digits.length - 10);
    return `+${countryCode} (${digits.slice(-10, -7)}) ${digits.slice(
      -7,
      -4
    )}-${digits.slice(-4)}`;
  }

  return phone;
};

export const formatApptDuration = (duration) => {
  return `${duration} ${duration === 1 ? "Hour" : "Hours"}`;
};

export const formatApptDate = (dateStr) => {
  const date = dayjs(dateStr);
  if (!date.isValid()) return "Invalid Date";
  return date.format(globalDateFormat1());
};

export const formatCurrentAgent = (currentAgentStatus) => {
  return (
    <div className="flex justify-center">
      {currentAgentStatus === "Completed" ? (
        <FaCheckSquare size={18} color="green" />
      ) : (
        <FaRegSquare size={18} className="text-gray-300" />
      )}
    </div>
  );
};

export const formatStatusBadge = (status) => {
  const statusColors = {
    Confirmed: { textColor: "text-green-500" },
    Confirm: { textColor: "text-green-500" },
    Completed: { textColor: "text-green-800" },
    Reschedule: { textColor: "text-red-500" },
    Cancel: { textColor: "text-red-500" },
    "No Response": { textColor: "text-red-500" },
    Pending: { textColor: "text-yellow-500" },
    "In Process": { textColor: "text-blue-500" },
  };
  return (
    <span
      className={`inline-flex py-1 font-semibold leading-5 text-md ${statusColors[status]?.textColor}`}
    >
      {status}
    </span>
  );
};

export const FormatLocalDateTime = () => {
  const [localTime, setLocalTime] = useState(dayjs());

  useEffect(() => {
    const userTimeZone = dayjs.tz.guess();
    const timer = setInterval(() => {
      setLocalTime(dayjs().tz(userTimeZone));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div>
      <p>Date: {localTime.format(globalDateFormat1())}</p>
      <p>Time: {localTime.format("hh:mm A")}</p>
    </div>
  );
};

export const FormatFollowUpStatus = ({
  value,
  followUpPatient,
  onFetchFollowUp,
}) => {
  const [localStatus, setLocalStatus] = useState(value);

  // ────────────────────── 45‑seconds countdown management ──────────────────────
  const intervalRef = useRef(null);
  const timersKey = "followUpTimers";
  const [timeLeft, setTimeLeft] = useState(null); // seconds

  const formatTime = (secs) => {
    const m = String(Math.floor(secs / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  function handleCheckBoxChange() {
    setLocalStatus((prevStatus) => {
      return prevStatus === "In Process"
        ? "Pending"
        : prevStatus === "Pending"
        ? "In Process"
        : prevStatus;
    });
  }

  const prevStatusRef = useRef(localStatus);

  // Restore a running timer only if status is "In Process" and timer is valid
  useEffect(() => {
    const timers = JSON.parse(localStorage.getItem(timersKey) || "{}");
    const expiry = timers[followUpPatient?.PatientID];

    if (localStatus === "In Process") {
      if (expiry && expiry > Date.now()) {
        // Restore timer only if previously saved
        setTimeLeft(Math.ceil((expiry - Date.now()) / 1000));
      } else {
        // Timer expired or was never set, mark as Completed
        setLocalStatus("Completed");
        delete timers[followUpPatient?.PatientID];
        localStorage.setItem(timersKey, JSON.stringify(timers));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followUpPatient?.PatientID]);

  useEffect(() => {
    const patientId = followUpPatient?.PatientID;
    const timers = JSON.parse(localStorage.getItem(timersKey) || "{}");

    if (localStatus === "In Process") {
      // Establish / reuse expiry
      let expiry = timers[patientId];
      if (!expiry || expiry <= Date.now()) {
        expiry = Date.now() + 1 * 45 * 1000; // 45-seconds from now
        timers[patientId] = expiry;
        localStorage.setItem(timersKey, JSON.stringify(timers));
      }

      // Kick off (or restart) countdown
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimeLeft(Math.ceil((expiry - Date.now()) / 1000));

      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            // Auto‑complete
            setLocalStatus("Completed");
            const updated = JSON.parse(localStorage.getItem(timersKey) || "{}");
            delete updated[patientId];
            localStorage.setItem(timersKey, JSON.stringify(updated));
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Pending or Completed → clear timer/interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setTimeLeft(null);
      if (timers[patientId]) {
        delete timers[patientId];
        localStorage.setItem(timersKey, JSON.stringify(timers));
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStatus, followUpPatient?.PatientID]);

  useEffect(() => {
    if (prevStatusRef.current !== localStatus) {
      onFetchFollowUp(followUpPatient?.PatientID, localStatus);
      prevStatusRef.current = localStatus;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStatus]);

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-2">
        {localStatus === "Completed" ? (
          <FaCheckSquare size={15} className="text-green-600" />
        ) : (
          <input
            type="checkbox"
            disabled={localStatus === "Completed"}
            className={`${
              localStatus === "Completed" ? "cursor-default" : "cursor-pointer"
            }`}
            checked={localStatus !== "Pending"}
            onChange={handleCheckBoxChange}
          />
        )}
        <span className="text-sm font-medium text-gray-700">
          {formatStatusBadge(localStatus)}
        </span>
      </div>
      {/* Countdown / warning */}
      {localStatus === "In Process" && timeLeft !== null && (
        <p className="text-xs font-semibold text-red-600">
          Undo Before {formatTime(timeLeft)}
        </p>
      )}
    </div>
  );
};

export const FormatCallStatusDetails = ({ appointment, pageName, isPlaying, handlePlayPause }) => {
  const [currentSummary, setCurrentSummary] = useState("");
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);

  const handleShowTranscript = useCallback((transcriptionText) => {
    setCurrentTranscript(transcriptionText);
    setShowTranscriptModal(true);
  }, []);

  const handleShowSummary = useCallback((summaryText) => {
    setCurrentSummary(summaryText);
    setShowSummaryModal(true);
  }, []);

  const handleHideTranscript = useCallback(() => {
    setShowTranscriptModal(false);
    setCurrentTranscript("");
  }, []);

  const handleHideSummary = useCallback(() => {
    setShowSummaryModal(false);
    setCurrentSummary("");
  }, []);

  return (
    <>
      {/* Transcript Modal */}
      <PageTableStatusModal
        show={showTranscriptModal}
        title="Call Transcript"
        onClose={handleHideTranscript}
      >
        <div className="text-left whitespace-pre-wrap">
          {currentTranscript || "No transcript available"}
        </div>
      </PageTableStatusModal>

      {/* Summary Modal */}
      <PageTableStatusModal
        show={showSummaryModal}
        title="AI Summary"
        onClose={handleHideSummary}
      >
        <div className="text-left whitespace-pre-wrap">
          {currentSummary || "No summary available"}
        </div>
      </PageTableStatusModal>

      {pageName === "CompletedCalls" ? (
        <p className="mb-2 font-bold text-green-800 text-md">Completed</p>
      ) : null}

      <div className="flex items-center justify-center space-x-2">
        {appointment?.Recordingpath && (
          <>
            <button
              onClick={handlePlayPause}
              className={`flex items-center border-2 justify-center w-8 h-8  rounded-full ${
                isPlaying
                  ? "bg-red-200 border-red-600 hover:bg-red-200"
                  : "bg-blue-200 border-blue-600 hover:bg-blue-200"
              }`}
              title={isPlaying ? "Pause Recording" : "Play Recording"}
            >
              {isPlaying ? (
                <FaStop className="w-3 h-3" color="red" />
              ) : (
                <FaPlay className="w-3 h-3" color="blue" />
              )}
            </button>
          </>
        )}
        {(appointment?.Transcript || appointment?.Transcription) && (
          <button
            onClick={() =>
              handleShowTranscript(
                appointment?.Transcript || appointment?.Transcription
              )
            }
            className="flex items-center justify-center w-8 h-8 text-gray-600 bg-green-100 border-2 border-green-600 rounded-full hover:bg-green-200"
            title="View Transcript"
          >
            <MdChatBubbleOutline className="w-4 h-4" />
          </button>
        )}
        {appointment?.AiSummary && (
          <button
            onClick={() => handleShowSummary(appointment?.AiSummary)}
            className="flex items-center justify-center w-8 h-8 text-purple-600 bg-purple-100 border-2 border-purple-600 rounded-full hover:bg-purple-200"
            title="View AI Summary"
          >
            <MdSummarize className="w-4 h-4" />
          </button>
        )}
      </div>
    </>
  );
};
